import { Building2, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export type ClientTypeKind = "individual" | "business";

export interface ClientTypeChoiceProps {
  /** Currently selected type; null when nothing has been picked yet. */
  value: ClientTypeKind | null;
  onChange: (value: ClientTypeKind) => void;
  disabled?: boolean;
}

interface TypeCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function TypeCard({ icon: Icon, title, description, selected, disabled, onClick }: TypeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 text-left transition-all focus-visible:outline-2 focus-visible:outline-primary",
        selected
          ? "border-primary bg-primary/5 ring-2 ring-primary/15"
          : "border-border bg-card hover:border-primary/40 hover:bg-accent/50",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "flex size-9 items-center justify-center rounded-lg bg-muted",
          selected ? "text-primary" : "text-foreground",
        )}
      >
        <Icon size={20} strokeWidth={1.9} />
      </span>
      <span>
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

/**
 * Client-type selection screen of the create flow (mock `client_type`): selectable Individual / Business
 * cards. Unlike the entity choice, picking a card only selects it — the drawer footer "Continue" advances
 * once a type is chosen (mock `canContinue = !!identityType`).
 */
export function ClientTypeChoice({ value, onChange, disabled }: ClientTypeChoiceProps) {
  const { t } = useTranslations("clients");

  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
      <TypeCard
        icon={User}
        title={t("table_type_individual")}
        description={t("client_type_card_individual_description")}
        selected={value === "individual"}
        disabled={disabled}
        onClick={() => onChange("individual")}
      />
      <TypeCard
        icon={Building2}
        title={t("table_type_empresa")}
        description={t("client_type_card_business_description")}
        selected={value === "business"}
        disabled={disabled}
        onClick={() => onChange("business")}
      />
    </div>
  );
}
